/**
 * Custom NumberStatsNPatComponent - Override Component
 * Auto generated overrides from shared library
 */
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { map } from 'rxjs';
import { NumberStatsNPatComponent as ParentNumberStatsNPatComponent } from '@shared-lib/global-components/number-stats-n-pat/number-stats-n-pat.component';

@Component({
  selector: 'app-number-stats-n-pat',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './number-stats-n-pat.component.html',
  styleUrls: ['./number-stats-n-pat.component.scss']
})
export class NumberStatsNPatComponent extends ParentNumberStatsNPatComponent {
  // Constructor is inherited from parent to avoid dependency injection issues

  override loadStats(filters?: any): any {
    return super.loadStats(filters).pipe(
      map((response: unknown) => this.applyConfiguredCards(response)),
    );
  }

  override trackByCard(_index: any, card: any): any {
    // TODO: Add your custom logic here
    return super.trackByCard(_index, card);
  }

  override trackByRow(_index: any, row: any): any {
    // TODO: Add your custom logic here
    return super.trackByRow(_index, row);
  }

  private applyConfiguredCards(response: unknown): unknown {
    const cardsConfig = Array.isArray(this.data?.chartConfig?.cards)
      ? this.data.chartConfig.cards
      : [];

    if (!cardsConfig.length) {
      return response;
    }

    const source = this.resolveStatsSource(response);
    const cards = cardsConfig.map((cardConfig: Record<string, unknown>) => {
      const rowsConfig = Array.isArray(cardConfig?.['rows']) ? cardConfig['rows'] as Array<Record<string, unknown>> : [];
      const rows = rowsConfig.map((rowConfig) => ({
        label: String(rowConfig['label'] ?? rowConfig['title'] ?? ''),
        value: this.formatConfiguredValue(
          this.resolveConfiguredValue(source, rowConfig),
          rowConfig,
        ),
      }));

      return {
        title: String(cardConfig?.['title'] ?? cardConfig?.['label'] ?? ''),
        ...(rows.length
          ? { rows }
          : {
              value: this.formatConfiguredValue(this.resolveConfiguredValue(source, cardConfig), cardConfig),
            }),
      };
    });

    return { cards };
  }

  private resolveConfiguredValue(source: unknown, config: Record<string, unknown>): unknown {
    const sumKeys = Array.isArray(config['sumKeys']) ? config['sumKeys'] : [];
    if (sumKeys.length) {
      return sumKeys.reduce((sum, key) => {
        const value = this.parseNumber(this.readPath(source, String(key)));
        return sum + (value ?? 0);
      }, 0);
    }

    const valueKey = String(config['valueKey'] ?? config['sourceKey'] ?? config['field'] ?? '');
    return this.readPath(source, valueKey);
  }

  private resolveStatsSource(response: unknown): unknown {
    if (this.isPlainRecord(response)) {
      return response['responseList']?.[0]?.['data']?.[0]
        ?? response['data']?.[0]
        ?? response['data']
        ?? response;
    }

    return response;
  }

  private readPath(source: unknown, path: string): unknown {
    if (!path.trim()) {
      return source;
    }

    return path.split('.').reduce<unknown>((current, segment) => {
      if (this.isPlainRecord(current)) {
        return current[segment];
      }

      if (Array.isArray(current)) {
        const index = Number(segment);
        return Number.isInteger(index) ? current[index] : undefined;
      }

      return undefined;
    }, source);
  }

  private formatConfiguredValue(value: unknown, config: Record<string, unknown>): string {
    if (value === undefined || value === null || value === '') {
      return '';
    }

    const numericValue = this.parseNumber(value);
    const decimals = this.toNonNegativeNumber(config['valueDecimals'] ?? config['decimals']);
    const prefix = this.optionalString(config['valuePrefix'] ?? config['prefix']) || '';
    const suffix = this.optionalString(config['valueSuffix'] ?? config['suffix']) || '';
    const valueScale = this.parseNumber(config['valueScale'] ?? config['scale']);
    const format = String(config['valueFormat'] ?? config['format'] ?? '').toLowerCase();
    const scaledValue = numericValue !== null && valueScale ? numericValue / valueScale : numericValue;

    if (scaledValue !== null && (format === 'compact' || format === 'compactcurrency')) {
      const formatted = new Intl.NumberFormat('en-US', {
        notation: 'compact',
        maximumFractionDigits: decimals ?? (Math.abs(scaledValue) >= 10 || scaledValue === 0 ? 0 : 1),
      }).format(scaledValue);
      return `${format === 'compactcurrency' && !prefix ? '$' : prefix}${formatted}${suffix}`;
    }

    if (scaledValue !== null && format === 'currency') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: this.optionalString(config['currency']) || 'USD',
        maximumFractionDigits: decimals ?? 0,
      }).format(scaledValue);
    }

    if (scaledValue !== null && (format === 'percent' || suffix === '%')) {
      const formatted = scaledValue.toLocaleString('en-US', {
        maximumFractionDigits: decimals ?? 1,
        minimumFractionDigits: decimals ?? 0,
      });
      return `${prefix}${formatted}%`;
    }

    if (scaledValue !== null) {
      const formatted = scaledValue.toLocaleString('en-US', {
        maximumFractionDigits: decimals ?? 0,
        minimumFractionDigits: decimals ?? 0,
      });
      return `${prefix}${formatted}${suffix}`;
    }

    return String(value);
  }

  private parseNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value !== 'string') {
      return null;
    }

    const numericText = value.replace(/,/g, '').match(/-?\d+(\.\d+)?/);
    if (!numericText) {
      return null;
    }

    const numericValue = Number(numericText[0]);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  private toNonNegativeNumber(value: unknown): number | null {
    const numericValue = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(numericValue) && numericValue >= 0 ? Math.round(numericValue) : null;
  }

  private optionalString(value: unknown): string | undefined {
    return value === undefined || value === null || value === '' ? undefined : String(value);
  }

  private isPlainRecord(value: unknown): value is Record<string, any> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }
}
